import cn from 'classnames'
import Link from 'next/link'
import React from 'react'

import { IntegrationCaseData } from '../../lib'
import IntegrationCasePreview from '../IntegrationCasePreview'
import styles from './ConnectIntegrationForm.module.scss'

import CalendarIcon from '~icons/newDesign/calendar-small.svg'
import { BOOK_CALL_LINK_INTEGRATION_SUPPORT } from '~lib/constants'
import Box from '~lib/ui-kit/Box'
import Button from '~shared/ui/Button'
import Divider from '~shared/ui/Divider'
import Flex from '~shared/ui/Flex'
import Text from '~shared/ui/Text'

interface ConnectIntegrationFormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  name: string
  ctaText: React.ReactNode
  cases: IntegrationCaseData[]
  error?: string
  loading?: boolean
  disabled?: boolean
}

function ConnectIntegrationForm({
  name,
  ctaText,
  cases,
  error,
  loading,
  children,
  className,
  disabled,
  ...props
}: React.PropsWithChildren<ConnectIntegrationFormProps>) {
  return (
    <Box as="section" className={cn(styles.root, className)}>
      <div className={styles.cases}>
        {cases.map(({ title, description, aspects, getIcon }) => {
          return (
            <IntegrationCasePreview
              key={title}
              title={title}
              description={description}
              aspects={aspects}
              icon={getIcon()}
              className="mb-4"
            />
          )
        })}
      </div>

      <Link href={BOOK_CALL_LINK_INTEGRATION_SUPPORT} target="_blank">
        <Button variant="link" after={<CalendarIcon />}>
          Book a call with integration support
        </Button>
      </Link>

      <Divider direction="horizontal" className="mt-3 mb-3" />

      <form className={styles.form} {...props}>
        <Text textType="heading-sm" variant="default" className="mb-2">
          Connect your {name} account
        </Text>

        <Text textType="body-md" variant="secondary" className="mb-2">
          {ctaText}
        </Text>

        {children && <div className="mb-2">{children}</div>}

        <Flex align="center" justify="start">
          <Button type="submit" variant="primary" className="px-3 py-1-5" disabled={disabled} loading={loading}>
            Connect
          </Button>
        </Flex>

        {error && (
          <Text variant="error" className="mt-1">
            {error}
          </Text>
        )}
      </form>
    </Box>
  )
}

export default ConnectIntegrationForm
